import { useEffect, useRef, useState } from "react";
import { drawHero } from "@/game/sprites";
import { Gem, Lock, Crown, Swords, Heart, HeartPlus, Gift, MapPin, Sparkles, Shirt } from "lucide-react";

const SKIN_NAMES = { chef_hat: "Chef Hat", knight_armor: "Knight Armor", pirate_hat: "Pirate Hat", cool_shades: "Cool Shades", wizard_hat: "Wizard Hat", royal_crown: "Royal Crown", cowboy_hat: "Cowboy Hat", viking_helmet: "Viking Helmet", ninja_mask: "Ninja Mask", superhero_cape: "Super Butter Cape" };
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { WEAPONS } from "@/game/config";
import { api } from "@/lib/api";
import { PixelButton } from "./PixelButton";

const SkinPreview = ({ skin }) => {
  const ref = useRef(null);
  useEffect(() => {
    const ctx = ref.current.getContext("2d");
    let raf, t = 0;
    const loop = () => {
      t += 0.016;
      ctx.clearRect(0, 0, 128, 128);
      ctx.save(); ctx.translate(64, 76 - Math.abs(Math.sin(t * 3)) * 6); ctx.scale(2.3, 2.3);
      drawHero(ctx, { x: 0, y: 0, dir: 1, walk: 0, onGround: true }, t, false, skin);
      ctx.restore();
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [skin]);
  return <canvas ref={ref} width={128} height={128} className="skin-preview" data-testid={`skin-preview-${skin}`} />;
};

const usd = (c) => `$${(c / 100).toFixed(2)}`;

const PackCard = ({ pack, onBuy, busy, locked }) => (
  <div className={`shop-card ${pack.vip ? "vip" : ""}`} data-testid={`gem-pack-${pack.lookup_key}`}>
    {pack.discount && <span className="deal-badge">-{pack.discount}%</span>}
    <Gem size={28} />
    <strong>{pack.gems.toLocaleString()} gems</strong>
    <small>{pack.name}</small>
    <PixelButton disabled={busy || locked} onClick={() => onBuy(pack)} data-testid={`buy-pack-${pack.lookup_key}`}>
      {locked ? <><Lock size={14} /> Locked</> : usd(pack.amount)}
    </PixelButton>
  </div>
);

const itemMeta = (item, p) => {
  if (item.kind === "weapon") return { icon: Swords, title: WEAPONS[item.value].name, desc: "Start every run with it.", owned: p.weapons.includes(item.value) };
  if (item.kind === "level") return { icon: MapPin, title: `Unlock Level ${item.value}`, desc: "Skip ahead: start runs here.", owned: p.max_level >= item.value };
  if (item.kind === "skin") return { icon: Shirt, title: SKIN_NAMES[item.value], desc: "Cosmetic outfit for Butter Boy.", owned: p.skins.includes(item.value) };
  if (item.kind === "extra_life") return { icon: Heart, title: "Extra Life", desc: `+1 life next run (${p.extra_lives}/${item.max})`, owned: p.extra_lives >= item.max };
  if (item.kind === "extra_heart") return { icon: HeartPlus, title: "Max Heart Up", desc: `Permanent +1 heart (${p.extra_hearts}/${item.max})`, owned: p.extra_hearts >= item.max };
  return { icon: Gift, title: "Bonus Pack", desc: `Every level next run: shield, magnet, x2 toast (${p.bonus_packs}/${item.max})`, owned: p.bonus_packs >= item.max };
};

const ItemCard = ({ item, player, onBuy, onEquip, busy }) => {
  const m = itemMeta(item, player);
  const Icon = m.icon;
  if (item.kind === "skin" && m.owned) {
    const on = player.equipped_skin === item.value;
    return (
      <div className="shop-item skin-owned" data-testid={`shop-item-${item.id}`}>
        <SkinPreview skin={item.value} />
        <div><strong>{m.title}</strong><small>{on ? "Wearing now" : "Owned"}</small></div>
        <PixelButton variant={on ? "primary" : "ghost"} disabled={busy} onClick={() => onEquip(on ? null : item.value)} data-testid={`equip-skin-${item.value}`}>
          {on ? "Unequip" : "Equip"}
        </PixelButton>
      </div>
    );
  }
  return (
    <div className={`shop-item ${m.owned ? "owned" : ""}`} data-testid={`shop-item-${item.id}`}>
      {item.kind === "skin" ? <SkinPreview skin={item.value} /> : <Icon size={20} />}
      <div><strong>{m.title}</strong><small>{m.desc}</small></div>
      <PixelButton variant="ghost" disabled={busy || m.owned || player.gems < item.cost} onClick={() => onBuy(item)} data-testid={`buy-item-${item.id}`}>
        {m.owned ? "Owned" : <><Gem size={14} /> {item.cost}</>}
      </PixelButton>
    </div>
  );
};

export const ShopModal = ({ open, onClose, player, onPlayer }) => {
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && !catalog) api.shop().then(setCatalog); }, [open, catalog]);
  if (!player) return null;

  const buyPack = async (pack) => {
    setBusy(true);
    try {
      const { checkout_url } = await api.checkout(player.name, pack.lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const buyItem = async (item) => {
    setBusy(true);
    try {
      onPlayer(await api.buy(player.name, item.id));
      toast.success("Purchased!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    } finally { setBusy(false); }
  };

  const equip = async (skin) => {
    setBusy(true);
    try { onPlayer(await api.equip(player.name, skin)); }
    catch (e) { toast.error("Could not change outfit"); }
    finally { setBusy(false); }
  };

  const spent = player.total_spent_cents;
  const threshold = catalog?.vip_threshold_cents || 2000;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="pixel-dialog max-w-4xl" data-testid="shop-modal">
        <DialogHeader>
          <DialogTitle className="dialog-title">Gem Shop</DialogTitle>
          <DialogDescription className="dialog-sub" data-testid="shop-gem-balance">
            <Gem size={14} className="inline" /> {player.gems.toLocaleString()} gems · {player.name}
          </DialogDescription>
        </DialogHeader>
        {!catalog ? <p className="dialog-sub">Loading...</p> : (
          <Tabs defaultValue="items">
            <TabsList className="shop-tabs">
              <TabsTrigger value="items" data-testid="shop-tab-items">Power-Ups</TabsTrigger>
              <TabsTrigger value="skins" data-testid="shop-tab-skins"><Shirt size={14} /> Outfits</TabsTrigger>
              <TabsTrigger value="gems" data-testid="shop-tab-gems">Buy Gems</TabsTrigger>
              <TabsTrigger value="vip" data-testid="shop-tab-vip"><Crown size={14} /> Special Offers</TabsTrigger>
            </TabsList>
            <TabsContent value="items" className="shop-items">
              {catalog.items.filter((i) => i.kind !== "skin").map((i) => <ItemCard key={i.id} item={i} player={player} onBuy={buyItem} busy={busy} />)}
            </TabsContent>
            <TabsContent value="skins" className="shop-items">
              {catalog.items.filter((i) => i.kind === "skin").map((i) => <ItemCard key={i.id} item={i} player={player} onBuy={buyItem} onEquip={equip} busy={busy} />)}
            </TabsContent>
            <TabsContent value="gems">
              <div className="shop-grid">
                {catalog.packs.filter((p) => !p.vip).map((p) => <PackCard key={p.lookup_key} pack={p} onBuy={buyPack} busy={busy} />)}
              </div>
              <p className="dialog-sub mt-4">Secure checkout by Stripe. Test card: 4242 4242 4242 4242.</p>
            </TabsContent>
            <TabsContent value="vip">
              <div className="vip-progress" data-testid="vip-progress">
                <Sparkles size={16} />
                {player.vip ? "VIP unlocked! Enjoy 20% and 50% off deals." : `Spend ${usd(threshold)} total to unlock. Spent so far: ${usd(spent)}`}
                <div className="toast-bar"><div style={{ width: `${Math.min(100, (spent / threshold) * 100)}%` }} /></div>
              </div>
              <div className="shop-grid">
                {catalog.packs.filter((p) => p.vip).map((p) => <PackCard key={p.lookup_key} pack={p} onBuy={buyPack} busy={busy} locked={!player.vip} />)}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
